import { webhook, WebhookConfig } from '@/utils/webhook';
import logger from '@/logger';
import { APITimeoutException } from './exceptions/APITimeoutException';
import TDSScheduleReducer from '@/apis/tdsReducer';
import StrapiReducer from '@/apis/strapiReducer';

export type Config = {
  body?: string;
  data?: object;
  headers?: HeadersInit;
  method: string;
  next?: NextFetchRequestConfig;
  params?: object;
  path: string;
  signal?: AbortSignal;
  retry?: boolean;
  credentials?: RequestCredentials;
};

const isWebstoreEndpoint = (path: string) => {
  const webstoreEndpoints = [
    'book',
    'cart',
    'customer/change-pw',
    'customer/detail',
    'customer/forgot-pw',
    'customer/payment/stored',
    'customer/reset-pw',
    'customer/update',
    'loyalty/total',
    'reservation',
    'user/',
    'user/login',
    'user/logout',
    'v2/availability',
    'v2/book',
    'v2/schedule',
    'v1/cancel/commuter'
  ];
  if (
    webstoreEndpoints.includes(path) ||
    path.startsWith('customer/payment/stored') ||
    path.startsWith('reservation/')
  ) {
    return true;
  }
  return false;
};

const fetchTWAPI = async (config: Config) => {
  config.headers = {
    Authorization: `Bearer ${process.env.TW_WEB_API_TOKEN}`
  };
  try {
    return await fetchJSON(config, process.env.TW_WEB_API_URL);
  } catch (error) {
    if (error instanceof APITimeoutException) {
      throw error;
    }
  }
};

const fetchSTRAPI = async (config: Config) => {
  config.headers = {
    Authorization: `Bearer ${process.env.STRAPI_API_TOKEN}`
  };
  try {
    if (typeof window !== 'undefined') {
      return await fetchJSON(config, `${window.location.origin}/api/cms`, null, ['strapi']);
    }
    return await fetchJSON(config, process.env.STRAPI_API_URL, null, ['strapi']);
  } catch (error) {
    if (error instanceof APITimeoutException) {
      throw error;
    }
  }
};

const fetchTDS = async (config: Config, webhookConfig?: WebhookConfig) => {
  if (typeof window !== 'undefined') {
    try {
      return await fetchJSON(config, `${window.location.origin}/api/tds`);
    } catch (error) {}
  }
  config.headers = {
    ...config.headers,
    'Content-Type': 'application/json',
    'tds-api-key': process.env.TDS_API_KEY || '',
    'tds-carrier-code': process.env.TDS_CARRIER_CODE || ''
  };
  if (process.env.MOCK_MODE) {
    config.headers = {
      ...config.headers,
      'x-api-key': process.env.MOCK_API_KEY || ''
    };
  }
  try {
    return await fetchJSON(config, process.env.TDS_API_URL, webhookConfig, ['tds']);
  } catch (error) {
    if (error instanceof APITimeoutException) {
      throw error;
    }
  }
};
const fetchTrailways = async (config: Config) => {
  try {
    return await fetchJSON(config, `${window.location.origin}/api`);
  } catch (error) {
    if (error instanceof APITimeoutException) {
      throw error;
    }
  }
};

const fetchJSON = async (
  config: Config,
  url: string | undefined,
  webhookConfig?: WebhookConfig | null,
  revalidateTags?: string[]
) => {
  if (config.data) {
    config.body = JSON.stringify(config.data);
  }
  if (revalidateTags) {
    config.next = {
      ...config.next,
      tags: revalidateTags
    };
  }
  const u = new URL(`${url}/${config.path}`);
  if (config.params) {
    Object.entries(config.params).forEach(([key, value]: string[]) => {
      u.searchParams.set(key, value);
    });
  }

  try {
    return await performRetryableFetch(u, config, webhookConfig);
  } catch (error) {
    // Allow one retry if specified
    if (config.retry) {
      logger.log('error', `Problem with fetch. Retrying for ${u.toString()}`);
      try {
        return await performRetryableFetch(u, config, webhookConfig);
      } catch (error) {
        if (error instanceof APITimeoutException) {
          throw error;
        }
      }
    } else {
      if (error instanceof APITimeoutException) {
        throw error;
      }
    }
  }
};

const performRetryableFetch = async (u: URL, config: Config, webhookConfig: WebhookConfig | null | undefined) => {
  let response: Response | null = null;

  // Allow for timeout via an AbortController
  const controller = new AbortController();
  config.signal = controller.signal;
  let timeout = process.env.NEXT_PUBLIC_API_TIMEOUT ? Number(process.env.NEXT_PUBLIC_API_TIMEOUT) * 1000 : 60 * 1000;
  const apiTimeout = setTimeout(() => {
    controller.abort();
  }, timeout);

  /*
    Change the `reservation/{confirmationCode}` request method to GET when called server-side
    to ensure compatibility with TDS. Also, delete `config.body` and retain `config.data` for the txn webhook.
  */
  if (config.path.startsWith('reservation/') && config.method === 'POST' && typeof window === 'undefined') {
    config.method = 'GET';
    delete config.body;
  }
  // Actual fetch
  return fetch(u.toString(), config)
    .then((apiResponse) => {
      /*
      if (apiResponse.status === 203) {
        console.log('Cache hit for', u.toString());
      } else {
        console.log('Cache miss for', u.toString());
      }
      */
      response = apiResponse;
      /*
        Return the actual `Response` object for these specific paths to ensure
        response cookies are available in the proxy API function.
      */
      if (
        ['cart', 'user/login', 'user/logout', 'v2/availability', 'reservation/assign-through-email'].includes(
          config.path
        ) &&
        response.ok
      ) {
        return apiResponse;
      }
      return apiResponse.json();
    })
    .then((json) => {
      clearTimeout(apiTimeout);
      // Fire webhook if the data isn't from cache
      if (webhookConfig && response && response.status !== 203) {
        const headers: { [key: string]: string } = {};
        response?.headers?.forEach((value: string, key: string) => {
          headers[key] = value;
        });
        const webhookPayload: { [key: string]: any } = {
          request: config.data,
          request_headers: webhookConfig?.requestHeaders
            ? { ...config.headers, ...webhookConfig.requestHeaders }
            : config.headers,
          response: json,
          response_headers: headers,
          ...(webhookConfig?.promoCode && {
            request_trailways: {
              promoCode: webhookConfig.promoCode,
              ...(webhookConfig.discountApplied && { discountApplied: webhookConfig.discountApplied }),
              ...(webhookConfig.quotedFare && { quotedFare: webhookConfig.quotedFare }),
              ...(webhookConfig.quotedFeeTotal && { quotedFeeTotal: webhookConfig.quotedFeeTotal })
            }
          })
        };
        if (config.path.startsWith('reservation/') && webhookConfig.endpoint === 'txn') {
          if (response.status === 200) {
            const { confirmationCode, customerEmailSent, description, requestPayload } = (
              config?.data as { [key: string]: any }
            ).bookingInfo;
            webhookPayload.request = requestPayload;
            webhookPayload.reservation = webhookPayload.response;
            webhookPayload.response = { confirmationCode, customerEmailSent, description };
          } else {
            delete webhookConfig.endpoint;
          }
        }
        if (config.path === 'book' && webhookConfig.endpoint === 'fxn') {
          if (response.status !== 200) {
            webhookPayload.response = {
              statusCode: response.status,
              contentType: headers?.['content-type'],
              payload: json
            };
          } else {
            delete webhookConfig.endpoint;
          }
        }
        if (webhookConfig.userAgent !== '') {
          webhook(webhookConfig, webhookPayload);
        }
        logger.log('info', `TDS request: ${u.toString()} with body ` + JSON.stringify(config.data));
      }

      if (response && response.status !== 203) {
        // Reduce size of TDS schedule data
        // Note that this is done here and in redis-cache-handler.js,
        // Redis-cache-handler.js handles the reduction for future cache hits
        // but we need to do it here for the not-yet-cached data
        if (json && json.scheduleProducts) {
          json = TDSScheduleReducer.reduce(json);
        }
        // Same for Strapi locations
        if (json && u.pathname.endsWith('/countries')) {
          json = StrapiReducer.reduceLocations(json);
        }
      }
      if (!response || !response.ok) {
        // Prep info on response
        let fullResponseInfo = '';
        if (response) {
          try {
            fullResponseInfo = JSON.stringify({
              status: response.status,
              statusText: response.statusText,
              body: JSON.stringify(json)
            });
          } catch (e) {
            fullResponseInfo = `${response?.status} ${response?.statusText}`; // in case stringify fails, at least get some info
          }
        }
        // Log the error
        const errorMessage = `Fetch Error in response for '${u.toString()}'.  Body '${
          config.body
        }'.  Response: ${fullResponseInfo}`;
        if (config.path !== 'customer/detail' && config.path !== 'loyalty/total') {
          // Logging these two would clutter the logs
          logger.log('error', errorMessage);
        }
        if (isWebstoreEndpoint(config.path)) {
          return {
            ...json,
            statusCode: response?.status
          };
        }
        throw new Error(errorMessage);
      }
      return json;
    })
    .catch(function (error) {
      // Prep info on exception
      let fullErrorInfo = '';
      if (error) {
        try {
          fullErrorInfo = JSON.stringify(error);
        } catch (e) {
          fullErrorInfo = error.message; // in case stringify fails, at least get some info
        }
      }
      // Prep info on response
      let fullResponseInfo = '';
      if (response) {
        try {
          fullResponseInfo = JSON.stringify({
            status: response.status,
            statusText: response.statusText,
            body: response.body
          });
        } catch (e) {
          fullResponseInfo = `${response?.status} ${response?.statusText}`; // in case stringify fails, at least get some info
        }
      }
      // Log the error
      const errorMessage = `Fetch error for '${u.toString()}'. Exception: ${fullErrorInfo}. Body: '${
        config.body
      }'. Response: ${fullResponseInfo}`;
      logger.log('error', errorMessage);
      if (error instanceof DOMException && error.name === 'AbortError') {
        logger.log('error', 'API call timed out for ' + u.toString());
        throw new APITimeoutException();
      } else {
        throw new Error(errorMessage);
      }
    });
};

const requestWithCredentials = async (url: string) => {
  const headers: HeadersInit = new Headers();
  headers.set('tds-api-key', process.env.TDS_API_KEY ?? '');
  headers.set('tds-carrier-code', process.env.TDS_CARRIER_CODE ?? '');
  const params: any = {
    body: null,
    credentials: 'include',
    headers: headers,
    method: 'GET'
  };
  const response = await fetch(url, params);
  return response;
};

export { fetchSTRAPI, fetchTDS, requestWithCredentials, fetchTWAPI, fetchTrailways };
